import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  OverlayView,
} from "@react-google-maps/api";
import { useGoogleMapsLoader } from "@/hooks/useGoogleMapsLoader";
import { X, Truck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";

const INDIA_CENTER = { lat: 22.5937, lng: 82.9629 };

const LiveTrackingMap = ({ vehicles, selectedVehicle, onSelectVehicle }) => {
  const { isLoaded } = useGoogleMapsLoader();

  const [map, setMap] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const lastCenteredIdRef = useRef(null);

  useEffect(() => {
    if (map && vehicles.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      let hasValidBounds = false;
      vehicles.forEach((v) => {
        if (v.location && v.location.includes(",")) {
          const [lat, lng] = v.location.split(",").map(Number);
          if (!isNaN(lat) && !isNaN(lng) && !(lat === 28.6139 && lng === 77.209)) {
            bounds.extend({ lat, lng });
            hasValidBounds = true;
          }
        }
      });
      if (hasValidBounds) {
        map.fitBounds(bounds);
        if (vehicles.length === 1) map.setZoom(14);
      }
    }
  }, [map, vehicles]);

  useEffect(() => {
    if (map && selectedVehicle && selectedVehicle.location) {
      if (lastCenteredIdRef.current !== selectedVehicle.id) {
        const [lat, lng] = selectedVehicle.location.split(",").map(Number);
        if (!isNaN(lat) && !isNaN(lng)) {
          map.panTo({ lat, lng });
          map.setZoom(14);
          lastCenteredIdRef.current = selectedVehicle.id;
          setActiveId(selectedVehicle.id);
        }
      }
    }
  }, [map, selectedVehicle]);

  if (!isLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-background rounded-2xl">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-medium text-muted-foreground">Loading Map...</p>
        </div>
      </div>
    );
  }

  const activeVehicle = vehicles.find((v) => v.id === activeId);
  const activeTrip = activeVehicle?.trip;
  const activeFirstItem = activeTrip?.items?.[0];
  const activeDriverName = activeTrip?.driver?.user?.username || activeTrip?.driver?.name || "Unknown";
  const activeProductName = activeTrip?.product_type?.name || activeFirstItem?.product_type?.name || "N/A";
  const activeConsigneeName = activeTrip?.consignee_name || activeFirstItem?.consignee_name || "N/A";

  const getPosition = (vehicle) => {
    if (vehicle && vehicle.location && vehicle.location.includes(",")) {
      const [lat, lng] = vehicle.location.split(",").map(Number);
      if (!isNaN(lat) && !isNaN(lng) && !(lat === 28.6139 && lng === 77.209)) {
        return { lat, lng };
      }
    }
    return null;
  };

  return (
    <div className="w-full h-full relative rounded-2xl overflow-hidden">
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={(selectedVehicle && getPosition(selectedVehicle)) || INDIA_CENTER}
        zoom={selectedVehicle ? 14 : 5}
        onLoad={(m) => setMap(m)}
        options={{
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: "greedy",
        }}
      >
        {vehicles.map((vehicle) => {
          const pos = getPosition(vehicle);
          if (!pos) return null;
          return (
            <OverlayView
              key={vehicle.id}
              position={pos}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={() => ({ x: -14, y: -14 })}
            >
              <div
                onClick={() => {
                  setActiveId(vehicle.id);
                  if (onSelectVehicle) onSelectVehicle(vehicle);
                }}
                className="cursor-pointer flex items-center justify-center"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  backgroundColor: "hsl(var(--primary))",
                  border: "2px solid white",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                }}
              >
                <Truck size={14} color="white" strokeWidth={2.5} />
              </div>
            </OverlayView>
          );
        })}

        <AnimatePresence>
          {activeVehicle && getPosition(activeVehicle) && (
            <OverlayView
              position={getPosition(activeVehicle)}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div className="relative flex flex-col items-center" style={{ transform: "translateY(-100%)" }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 20 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  className="relative rounded-2xl shadow-xl p-4 w-[360px] border"
                  style={{
                    backgroundColor: "hsl(var(--card))",
                    borderColor: "hsl(var(--border))",
                  }}
                >
                  <button
                    onClick={() => setActiveId(null)}
                    className="absolute right-3 top-3 p-1 rounded-full hover:bg-muted transition-colors cursor-pointer"
                  >
                    <X size={16} className="text-muted-foreground" />
                  </button>

                  <div className="flex justify-between items-start pr-6">
                    <div className="flex gap-3 items-center">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center border border-border">
                        <Truck size={18} className="text-primary" />
                      </div>
                      <div>
                        <h4 className="text-[14px] font-medium leading-tight text-foreground">
                          {activeVehicle.name}
                        </h4>
                        <p className="text-[12px] text-muted-foreground mt-0.5">
                          Trip: {activeVehicle.trip?.trip_number || "N/A"}
                        </p>
                      </div>
                    </div>
                    <Badge variant="success" size="xs" className="py-0.5 px-2 text-[9px] font-medium uppercase tracking-wider shrink-0">
                      {activeVehicle.status}
                    </Badge>
                  </div>

                  <div className="h-px w-full my-3" style={{ backgroundColor: "hsl(var(--border))" }}></div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[11px] text-muted-foreground mb-0.5">Driver</p>
                      <p className="text-[13px] font-medium text-foreground">
                        {activeDriverName}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground mb-0.5">Product</p>
                      <p className="text-[13px] font-medium text-foreground">
                        {activeProductName}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <p className="text-[11px] text-muted-foreground mb-0.5">Consignee</p>
                      <p className="text-[13px] font-medium text-foreground leading-snug truncate">
                        {activeConsigneeName}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground mb-0.5">Latest Updated</p>
                      <p className="text-[13px] font-medium text-foreground">
                        {activeVehicle.trip?.latest_updated
                          ? new Date(activeVehicle.trip.latest_updated).toLocaleString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="absolute left-1/2 -bottom-2 -translate-x-1/2 w-4 h-4 rotate-45 border-b border-r"
                    style={{
                      backgroundColor: "hsl(var(--card))",
                      borderColor: "hsl(var(--border))",
                    }}
                  ></div>
                </motion.div>
              </div>
            </OverlayView>
          )}
        </AnimatePresence>
      </GoogleMap>
    </div>
  );
};

export default LiveTrackingMap;